import React from 'react';
import { Tooltip } from 'antd';
import { BulbOutlined, EditOutlined, FileImageOutlined, SearchOutlined, TranslationOutlined } from '@ant-design/icons';
import {
  isMissingTranslatedMetadataValue,
} from '../utils/localizedMetadataDisplay';

export type LocalizedMetadataFieldStatus = {
  key: 'title' | 'series' | 'blurb';
  label: string;
  value: string;
  originalValue?: string;
};

interface LocalizedMetadataWorkspaceProps {
  language: string;
  authorName?: string;
  fields: LocalizedMetadataFieldStatus[];
  keywordCount?: number;
  coverReadiness?: LocalizedMetadataCoverReadiness;
  coverStatusLabel?: string;
  glossaryReady?: boolean;
  glossaryStatusLabel?: string;
  showGuidanceReadiness?: boolean;
  guidanceReady?: boolean;
  guidanceStatusLabel?: string;
  onSuggestKeywords?: () => void;
  onTranslateCover?: () => void;
  onStartGlossaryScan?: () => void;
  metadataContent: (
    mode: LocalizedMetadataContentMode,
    options?: LocalizedMetadataContentOptions,
  ) => React.ReactNode;
}

export type LocalizedMetadataState = 'not_started' | 'partial' | 'complete';
export type LocalizedMetadataContentMode =
  | 'hidden'
  | 'manual'
  | 'existing'
  | 'all'
  | 'cover'
  | 'storeListing'
  | 'blurb'
  | 'rules'
  | 'keywords';
export type LocalizedMetadataCoverReadiness = 'ready' | 'missing' | 'using_original';
export type LocalizedMetadataContentOptions = {
  onManualMetadataSaved?: () => void;
};

export const hasLocalizedMetadataValue = (value: string) => !isMissingTranslatedMetadataValue(value);

export const resolveLocalizedMetadataState = (fields: LocalizedMetadataFieldStatus[]): LocalizedMetadataState => {
  const completedCount = fields.filter(field => hasLocalizedMetadataValue(field.value)).length;
  if (completedCount === 0) return 'not_started';
  if (completedCount === fields.length) return 'complete';
  return 'partial';
};

type LocalizedMetadataFieldViewModel = LocalizedMetadataFieldStatus & {
  hasValue: boolean;
};

type LocalizedMetadataReadinessItem = {
  key: string;
  label: string;
  statusLabel: 'Ready' | 'Missing' | string;
  done: boolean;
  tone: 'ready' | 'needed' | 'attention';
  actionLabel?: string;
  actionDescription?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
};

export const buildLocalizedMetadataFieldViewModels = (
  fields: LocalizedMetadataFieldStatus[],
): LocalizedMetadataFieldViewModel[] => fields.map(field => ({
  ...field,
  hasValue: hasLocalizedMetadataValue(field.value),
}));

const formatLocalizedMetadataReadinessChipLabel = (item: LocalizedMetadataReadinessItem) => {
  if (item.statusLabel !== 'Ready' && item.statusLabel !== 'Missing') {
    const readySuffix = item.done && !/ready$/i.test(item.statusLabel) ? ' ready' : '';
    return `${item.label}: ${item.statusLabel}${readySuffix}`;
  }
  return `${item.label}: ${item.done ? 'Ready' : 'Missing'}`;
};

const LocalizedMetadataReadinessStrip: React.FC<{
  language: string;
  items: LocalizedMetadataReadinessItem[];
}> = ({ language, items }) => {
  const readyCount = items.filter(item => item.done).length;
  return (
    <section
      className="localized-metadata-readiness-strip dashboard-readiness-strip book-detail-workbench-section"
      data-testid="tutorial:metadata:translated-readiness"
    >
      <div className="dashboard-workflow-summary">
        <div className="dashboard-readiness-strip-header">
          <span>{language} metadata readiness</span>
          <strong>{readyCount}/{items.length} ready</strong>
        </div>
        <div className="dashboard-step-indicator localized-metadata-readiness-chips">
          {items.map(item => {
            const chipLabel = formatLocalizedMetadataReadinessChipLabel(item);
            const accessibleLabel = `${chipLabel}${item.actionDescription ? `. ${item.actionDescription}` : ''}`;
            return (
              <Tooltip key={item.key} title={accessibleLabel}>
                <button
                  type="button"
                  aria-label={accessibleLabel}
                  className={`dashboard-readiness-chip dashboard-step-indicator-item dashboard-workflow-step-chip dashboard-readiness-chip--${item.tone} ${item.done ? 'is-complete' : item.tone === 'attention' ? 'is-attention' : 'is-current'} localized-metadata-readiness-chip${item.onClick ? '' : ' localized-metadata-readiness-chip--static'}`}
                  onClick={item.onClick}
                  disabled={!item.onClick}
                >
                  <span aria-hidden="true">{item.icon}</span>
                  <strong>{chipLabel}</strong>
                </button>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const LocalizedMetadataWorkspace: React.FC<LocalizedMetadataWorkspaceProps> = ({
  language,
  authorName,
  fields,
  keywordCount = 0,
  coverReadiness = 'missing',
  coverStatusLabel,
  glossaryReady = false,
  glossaryStatusLabel,
  showGuidanceReadiness = false,
  guidanceReady = true,
  guidanceStatusLabel,
  onSuggestKeywords,
  onTranslateCover,
  onStartGlossaryScan,
  metadataContent,
}) => {
  const fieldViewModels = React.useMemo(() => buildLocalizedMetadataFieldViewModels(fields), [fields]);

  const focusWorkspaceSection = React.useCallback((selector: string) => {
    const target = document.querySelector(selector);
    if (!(target instanceof HTMLElement)) return;
    target.scrollIntoView({ block: 'start', behavior: 'smooth' });
    window.setTimeout(() => target.focus({ preventScroll: true }), 120);
  }, []);

  const focusLocalizedMetadataField = React.useCallback((fieldKey: LocalizedMetadataFieldStatus['key']) => {
    const fieldTarget = document.querySelector(`[data-localized-metadata-field="${fieldKey}"]`);
    const sectionTarget = document.querySelector(
      fieldKey === 'blurb'
        ? '[data-tour-id="metadata-translation-blurb-section"]'
        : '[data-tour-id="metadata-translation-section"]',
    );
    const target = fieldTarget || sectionTarget;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    window.setTimeout(() => {
      const focusTarget = target.querySelector(
        '.ql-editor, input, textarea, button, [role="button"], [contenteditable="true"], [tabindex]:not([tabindex="-1"])',
      );
      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus({ preventScroll: true });
        return;
      }
      target.focus({ preventScroll: true });
    }, 120);
  }, []);

  const metadataReadinessItems = React.useMemo<LocalizedMetadataReadinessItem[]>(() => {
    const prerequisiteItems: LocalizedMetadataReadinessItem[] = [
      ...(showGuidanceReadiness ? [{
        key: 'guidance',
        label: 'Guidance',
        statusLabel: guidanceReady ? (guidanceStatusLabel || 'Ready') : 'Missing',
        done: guidanceReady,
        tone: guidanceReady ? 'ready' as const : 'needed' as const,
        actionLabel: 'Set guidance',
        actionDescription: `Set language guidance before translating the ${language} edition.`,
      }] : []),
      {
        key: 'glossary',
        label: 'Glossary',
        statusLabel: glossaryReady ? (glossaryStatusLabel || 'Ready') : 'Missing',
        done: glossaryReady,
        tone: glossaryReady ? 'ready' : 'needed',
        actionLabel: 'Start AI Glossary Scan',
        actionDescription: `Create glossary rules before translating the ${language} edition.`,
        onClick: glossaryReady ? undefined : onStartGlossaryScan,
        icon: <SearchOutlined />,
      },
    ];
    const metadataItems = fieldViewModels.map(field => ({
      key: field.key,
      label: field.label,
      statusLabel: field.hasValue ? 'Ready' : 'Missing',
      done: field.hasValue,
      tone: field.hasValue ? 'ready' as const : 'needed' as const,
      actionLabel: `Review ${field.label.toLowerCase()}`,
      actionDescription: `Review the ${field.label.toLowerCase()} field in this store listing.`,
      onClick: () => focusLocalizedMetadataField(field.key),
      icon: field.hasValue ? <EditOutlined /> : <TranslationOutlined />,
    }));
    const keywordsReady = keywordCount > 0;
    const coverReady = coverReadiness === 'ready';
    const coverUsingOriginal = coverReadiness === 'using_original';
    return [
      ...prerequisiteItems,
      ...metadataItems,
      {
        key: 'keywords',
        label: 'Keywords',
        statusLabel: keywordsReady ? 'Ready' : 'Missing',
        done: keywordsReady,
        tone: keywordsReady ? 'ready' : 'needed',
        actionLabel: keywordsReady ? `View/edit ${keywordCount} keywords` : 'Create localized keywords',
        actionDescription: keywordsReady
          ? `Open the localized keywords for the ${language} edition.`
          : `Create localized keywords from the source metadata for the ${language} edition.`,
        onClick: keywordsReady
          ? () => focusWorkspaceSection('[data-tour-id="metadata-keywords-section"]')
          : onSuggestKeywords,
        icon: <BulbOutlined />,
      },
      {
        key: 'cover',
        label: 'Cover',
        statusLabel: coverReady ? 'Ready' : coverUsingOriginal ? (coverStatusLabel || 'Using original cover') : 'Missing',
        done: coverReady,
        tone: coverReady ? 'ready' : coverUsingOriginal ? 'attention' : 'needed',
        actionLabel: 'Translate cover',
        actionDescription: coverUsingOriginal
          ? `Create a localized cover instead of using the source-language cover.`
          : `Create a localized cover for the ${language} edition.`,
        onClick: coverReady ? undefined : onTranslateCover,
        icon: <FileImageOutlined />,
      },
    ];
  }, [
    coverReadiness,
    coverStatusLabel,
    fieldViewModels,
    focusWorkspaceSection,
    focusLocalizedMetadataField,
    glossaryReady,
    glossaryStatusLabel,
    guidanceReady,
    guidanceStatusLabel,
    keywordCount,
    language,
    onStartGlossaryScan,
    onSuggestKeywords,
    onTranslateCover,
    showGuidanceReadiness,
  ]);

  const titleField = fieldViewModels.find(field => field.key === 'title');
  const translatedTitle = titleField?.hasValue ? titleField.value : '';
  const displayAuthor = authorName?.trim() || 'Author not set';

  return (
    <div className="metadata-content-shell metadata-localized-workspace" data-testid="tutorial:metadata:translated-workspace">
      <div className="metadata-content-scroll">
        <section className="metadata-localized-workbench dashboard-dominant-surface book-detail-primary-surface book-detail-workbench-stack">
          <div className="metadata-original-command-row metadata-localized-command-row book-detail-workbench-section">
            <div className="metadata-original-source-summary metadata-localized-source-summary">
              <div className="metadata-original-source-main metadata-localized-source-main">
                <div className="metadata-original-cover-command metadata-localized-cover-command">
                  {metadataContent('cover')}
                </div>
                <div className="metadata-original-source-copy metadata-localized-source-copy dashboard-language-command-copy">
                  <div className="book-detail-eyebrow dashboard-section-kicker">{language} edition</div>
                  <div className="dashboard-language-title-row">
                    <h2 className={!translatedTitle ? 'metadata-localized-title-missing' : undefined}>
                      {translatedTitle || '<Title not translated>'}
                    </h2>
                  </div>
                  <p className="dashboard-cover-author">{displayAuthor}</p>
                </div>
              </div>
              <div data-testid="tutorial:metadata:translated-blurb">
                {metadataContent('blurb')}
              </div>
            </div>
            <div className="metadata-original-command-stack metadata-localized-command-stack">
              <LocalizedMetadataReadinessStrip
                language={language}
                items={metadataReadinessItems}
              />
              {metadataContent('storeListing')}
              <div data-testid="tutorial:metadata:translated-rules">
                {metadataContent('rules')}
              </div>
              <div data-testid="tutorial:metadata:translated-keywords">
                {metadataContent('keywords')}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LocalizedMetadataWorkspace;
